import React, { useState, useEffect } from "react";
import { supabase, isSupabaseConfigured } from "./supabaseClient";

const SOURCE_OPTIONS = [
  { name: "Fox News", color: "#003366" },
  { name: "BBC News", color: "#BB1919" },
  { name: "CNN", color: "#CC0000" },
  { name: "Al Jazeera", color: "#D4A843" },
  { name: "Daily Mail", color: "#1A1A6C" },
  { name: "New York Times", color: "#1A1A1A" },
  { name: "Washington Post", color: "#2D2D2D" },
  { name: "RT (Russia Today)", color: "#5A8F29" },
];

function AdminPanel({ onBack }) {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState("");
  const [analyses, setAnalyses] = useState([]);
  const [editing, setEditing] = useState(null); // null = list, 'new' = new form, object = editing
  const [message, setMessage] = useState("");
  const configured = isSupabaseConfigured();

  // Simple password auth (for now)
  const handleLogin = () => {
    if (password === "decode2026admin") {
      setIsLoggedIn(true);
      setMessage("");
    } else {
      setMessage("Wrong password");
    }
  };

  // Fetch all analyses (including unpublished)
  useEffect(() => {
    if (!isLoggedIn) return;
    fetchAnalyses();
  }, [isLoggedIn]);

  const fetchAnalyses = async () => {
    if (configured) {
      const { data } = await supabase
        .from("analyses")
        .select("*")
        .order("number", { ascending: true });
      if (data) setAnalyses(data);
    }
  };

  // Login screen
  if (!isLoggedIn) {
    return (
      <div className="app" style={{ padding: 20 }}>
        <button className="detail-back" onClick={onBack}>
          {"\u2190"} Back to App
        </button>
        <div style={{ maxWidth: 400, margin: "60px auto", textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>{"\u{1F512}"}</div>
          <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>Admin Panel</h2>
          <p style={{ fontSize: 13, color: "#6b6b80", marginBottom: 24 }}>
            Content management for Between the News
          </p>
          {!configured && (
            <div style={{
              background: "#2a1a1a",
              border: "1px solid #e63946",
              borderRadius: 12,
              padding: 16,
              marginBottom: 20,
              fontSize: 13,
              color: "#ff6b6b",
              lineHeight: 1.6
            }}>
              Supabase is not configured yet. Set REACT_APP_SUPABASE_URL and
              REACT_APP_SUPABASE_ANON_KEY in your .env file. For now, the app uses static data.
            </div>
          )}
          <input
            type="password"
            className="comment-input"
            placeholder="Admin password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            style={{ width: "100%", marginBottom: 12 }}
          />
          <button
            className="comment-submit"
            onClick={handleLogin}
            style={{ width: "100%", padding: 14, fontSize: 15 }}
          >
            Login
          </button>
          {message && (
            <p style={{ color: "#e63946", marginTop: 12, fontSize: 13 }}>{message}</p>
          )}
        </div>
      </div>
    );
  }

  // Edit/New form
  if (editing !== null) {
    return (
      <AnalysisForm
        analysis={editing === "new" ? null : editing}
        onSave={async (data) => {
          if (configured) {
            if (editing === "new") {
              await supabase.from("analyses").insert(data);
            } else {
              await supabase.from("analyses").update(data).eq("id", editing.id);
            }
            await fetchAnalyses();
          }
          setEditing(null);
          setMessage(editing === "new" ? "Analysis created!" : "Analysis updated!");
          setTimeout(() => setMessage(""), 3000);
        }}
        onCancel={() => setEditing(null)}
      />
    );
  }

  // Analysis list
  return (
    <div className="app" style={{ padding: 20, paddingBottom: 100 }}>
      <button className="detail-back" onClick={onBack}>
        {"\u2190"} Back to App
      </button>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 800 }}>Admin Panel</h2>
          <p style={{ fontSize: 12, color: "#6b6b80" }}>
            {configured ? "Connected to Supabase" : "Using static data (Supabase not configured)"}
          </p>
        </div>
        <button
          onClick={() => setEditing("new")}
          style={{
            background: "#e63946",
            color: "white",
            padding: "10px 20px",
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          + New Analysis
        </button>
      </div>

      {message && (
        <div style={{
          background: "rgba(46, 204, 113, 0.15)",
          color: "#2ecc71",
          padding: 12,
          borderRadius: 10,
          fontSize: 13,
          marginBottom: 16,
          textAlign: "center",
          fontWeight: 600
        }}>
          {message}
        </div>
      )}

      {!configured && (
        <div style={{
          background: "#1a1a2e",
          border: "1px solid #2a2a3e",
          borderRadius: 12,
          padding: 20,
          marginBottom: 20,
        }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: "#e63946", marginBottom: 10 }}>
            Setup Guide
          </h3>
          <ol style={{ fontSize: 13, color: "#9a9ab0", lineHeight: 2, paddingLeft: 20 }}>
            <li>Go to <strong>supabase.com</strong> and create a free account</li>
            <li>Create a new project (name: decode-the-news)</li>
            <li>Go to <strong>SQL Editor</strong> and paste the contents of <code>supabase-schema.sql</code></li>
            <li>Go to <strong>Settings &rarr; API</strong> and copy your URL and anon key</li>
            <li>Create a <code>.env</code> file in the project root (copy from <code>.env.example</code>)</li>
            <li>Restart the app with <code>npm start</code></li>
          </ol>
        </div>
      )}

      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>
        Analyses ({analyses.length})
      </div>

      {analyses.length === 0 && !configured && (
        <div style={{
          background: "#1a1a2e",
          borderRadius: 12,
          padding: 30,
          textAlign: "center",
          color: "#6b6b80",
          fontSize: 13
        }}>
          Connect Supabase to manage analyses here.
          <br />The app is currently showing static demo data.
        </div>
      )}

      {analyses.map((a) => (
        <div
          key={a.id}
          style={{
            background: "#1a1a2e",
            borderRadius: 12,
            padding: 16,
            marginBottom: 10,
            border: "1px solid #2a2a3e",
            cursor: "pointer",
          }}
          onClick={() => setEditing(a)}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <span className="analysis-number">#{a.number}</span>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{a.source}</span>
            <span className={`category-badge ${a.category.toLowerCase()}`}>{a.category}</span>
            {!a.is_published && (
              <span style={{ fontSize: 10, color: "#e63946", fontWeight: 700, marginLeft: "auto" }}>DRAFT</span>
            )}
            {a.is_published && (
              <span style={{ fontSize: 10, color: "#2ecc71", fontWeight: 700, marginLeft: "auto" }}>LIVE</span>
            )}
          </div>
          <div style={{ fontSize: 13, color: "#9a9ab0", lineHeight: 1.4 }}>
            {a.headline.substring(0, 100)}...
          </div>
        </div>
      ))}
    </div>
  );
}

// ===== ANALYSIS FORM =====
function AnalysisForm({ analysis, onSave, onCancel }) {
  const [form, setForm] = useState({
    number: analysis?.number || "",
    source: analysis?.source || "Fox News",
    source_color: analysis?.source_color || "#003366",
    category: analysis?.category || "Politics",
    headline: analysis?.headline || "",
    date: analysis?.date || new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }),
    read_time: analysis?.read_time || "3 min read",
    fact_tags: analysis?.fact_tags?.join(", ") || "",
    decoded: analysis?.decoded || "",
    pull_quote: analysis?.pull_quote || "",
    ap_comparison: analysis?.ap_comparison || "",
    is_published: analysis?.is_published ?? true,
    is_premium: analysis?.is_premium ?? false,
  });

  const handleSourceChange = (sourceName) => {
    const source = SOURCE_OPTIONS.find((s) => s.name === sourceName);
    setForm((prev) => ({
      ...prev,
      source: sourceName,
      source_color: source?.color || "#003366",
    }));
  };

  const handleSubmit = () => {
    if (!form.number || !form.headline || !form.decoded) {
      alert("Please fill in: Number, Headline, and Decoded analysis");
      return;
    }
    onSave({
      ...form,
      fact_tags: form.fact_tags.split(",").map((t) => t.trim()).filter(Boolean),
    });
  };

  const fieldStyle = {
    width: "100%",
    padding: "10px 14px",
    background: "#252538",
    border: "1px solid #2a2a3e",
    borderRadius: 10,
    color: "#e8e8e8",
    fontSize: 13,
    outline: "none",
    fontFamily: "inherit",
    marginBottom: 4,
  };

  const labelStyle = {
    fontSize: 12,
    fontWeight: 700,
    color: "#9a9ab0",
    marginBottom: 4,
    marginTop: 14,
    display: "block",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  };

  return (
    <div className="app" style={{ padding: 20, paddingBottom: 100 }}>
      <button className="detail-back" onClick={onCancel}>
        {"\u2190"} Cancel
      </button>

      <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 4 }}>
        {analysis ? `Edit Analysis #${analysis.number}` : "New Analysis"}
      </h2>
      <p style={{ fontSize: 12, color: "#6b6b80", marginBottom: 16 }}>
        {analysis ? "Edit and save your changes" : "Create a new media analysis"}
      </p>

      <label style={labelStyle}>Analysis Number *</label>
      <input
        style={fieldStyle}
        placeholder="e.g. 004"
        value={form.number}
        onChange={(e) => setForm({ ...form, number: e.target.value })}
      />

      <div style={{ display: "flex", gap: 10 }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Source *</label>
          <select
            style={{ ...fieldStyle, cursor: "pointer" }}
            value={form.source}
            onChange={(e) => handleSourceChange(e.target.value)}
          >
            {SOURCE_OPTIONS.map((s) => (
              <option key={s.name} value={s.name}>{s.name}</option>
            ))}
          </select>
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Category *</label>
          <select
            style={{ ...fieldStyle, cursor: "pointer" }}
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
          >
            <option value="Politics">Politics</option>
            <option value="Weird">Weird</option>
          </select>
        </div>
      </div>

      <label style={labelStyle}>Headline *</label>
      <textarea
        style={{ ...fieldStyle, minHeight: 70, resize: "vertical" }}
        placeholder='e.g. "Fox News headline here in quotes"'
        value={form.headline}
        onChange={(e) => setForm({ ...form, headline: e.target.value })}
      />

      <label style={labelStyle}>Date</label>
      <input
        style={fieldStyle}
        placeholder="March 28, 2026"
        value={form.date}
        onChange={(e) => setForm({ ...form, date: e.target.value })}
      />

      <label style={labelStyle}>Read Time</label>
      <input
        style={fieldStyle}
        placeholder="3 min read"
        value={form.read_time}
        onChange={(e) => setForm({ ...form, read_time: e.target.value })}
      />

      <label style={labelStyle}>Fact Tags (comma separated)</label>
      <input
        style={fieldStyle}
        placeholder="Tag 1, Tag 2, Tag 3, Owner: Murdoch family"
        value={form.fact_tags}
        onChange={(e) => setForm({ ...form, fact_tags: e.target.value })}
      />

      <label style={labelStyle}>Decoded Analysis *</label>
      <textarea
        style={{ ...fieldStyle, minHeight: 200, resize: "vertical", lineHeight: 1.6 }}
        placeholder="Your critical analysis of how this outlet framed the story..."
        value={form.decoded}
        onChange={(e) => setForm({ ...form, decoded: e.target.value })}
      />

      <label style={labelStyle}>Pull Quote</label>
      <textarea
        style={{ ...fieldStyle, minHeight: 60, resize: "vertical" }}
        placeholder="The key quote that summarizes the analysis"
        value={form.pull_quote}
        onChange={(e) => setForm({ ...form, pull_quote: e.target.value })}
      />

      <label style={labelStyle}>AP / Reuters Comparison</label>
      <textarea
        style={{ ...fieldStyle, minHeight: 80, resize: "vertical" }}
        placeholder='AP reported: "..." Fox reported: "..." Same event, different framing.'
        value={form.ap_comparison}
        onChange={(e) => setForm({ ...form, ap_comparison: e.target.value })}
      />

      <div style={{ display: "flex", gap: 20, marginTop: 16 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#9a9ab0", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={form.is_published}
            onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
            style={{ width: 18, height: 18 }}
          />
          Published (visible to users)
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#9a9ab0", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={form.is_premium}
            onChange={(e) => setForm({ ...form, is_premium: e.target.checked })}
            style={{ width: 18, height: 18 }}
          />
          Premium only
        </label>
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
        <button
          onClick={handleSubmit}
          style={{
            flex: 1,
            background: "#e63946",
            color: "white",
            padding: 14,
            borderRadius: 10,
            fontSize: 15,
            fontWeight: 700,
          }}
        >
          {analysis ? "Save Changes" : "Create Analysis"}
        </button>
        <button
          onClick={onCancel}
          style={{
            padding: "14px 24px",
            background: "#252538",
            color: "#9a9ab0",
            borderRadius: 10,
            fontSize: 15,
            fontWeight: 600,
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export default AdminPanel;
